// Vue
import { ref, watch } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { CreateWizardStepType } from "@/features/BuildingProject/configuration/createWizardSteps";

// Utils
import { advanceThroughPreAnswered, canJumpToStep, preAnsweredStepIds } from "@/features/BuildingProject/utils/wizardStepGating";

// Providers
import { useLocalization } from "@/providers/Localization";
import { useCreateBuildingProject } from "@/features/BuildingProject";
import { useStageDefaultEmissions } from "@/dataProviders/StageDefaultEmissions";

const useCreateBuildingProjectWizard = defineStore("CreateBuildingProjectWizardProvider", () => {
  // Data

  /** Where reset() parks the wizard until init() overwrites it — must be a step id every create flow's
   * wizardFlow contains, or getWizardStepFromId returns {}. */
  const firstStep = "PROJECT_DETAILS";

  // Providers

  const localization = useLocalization();
  const createBuildingProject = useCreateBuildingProject();
  const stageDefaultEmissions = useStageDefaultEmissions();

  // Refs

  const wizardFlow = ref<Array<CreateWizardStepType>>(localization.getCreateWizardFlow());
  const currentStep = ref<string>(firstStep);
  const stepsDone = ref<Array<string>>([]);
  /** Which steps the user has actually been on — distinct from stepsDone, which decides what is
   * unlocked and must keep containing auto-passed steps; this is a display-only record. */
  const stepsVisited = ref<Array<string>>([]);
  const speckleConnectionChoice = ref<number | null>();
  const triedToValidate = ref<boolean>(false);

  // Getters

  const getCurrentStep = () => {
    return currentStep.value;
  };

  const getStepsDone = () => {
    return stepsDone.value;
  };

  const getStepsVisited = () => {
    return stepsVisited.value;
  };

  const getSpeckleConnectionChoice = () => {
    return speckleConnectionChoice.value;
  };

  const getTriedToValidate = () => {
    return triedToValidate.value;
  };

  const getConditionalWizardFlow = () => {
    if (!createBuildingProject.getProject() && !wizardFlow.value) return [];
    return wizardFlow.value.filter((step) =>
      step.options.projectCondition ? step.options.projectCondition(createBuildingProject.getProject()) : true
    );
  };

  const getStepOverviewWizardFlow = () => {
    if (!createBuildingProject.getProject() && !wizardFlow.value) return [];
    return getConditionalWizardFlow().filter((step) => step.options.includeInStepOverview);
  };

  /** Every step this country's overview can show, ignoring current answers — the progress-bar denominator.
   * Must not be the conditional flow: that changes length mid-flow (Calculation prerequisites decide A4/A5/B6), which would send the fill backwards as more work becomes visible. */
  const getStepOverviewOrder = () => {
    if (!wizardFlow.value) return [];
    return wizardFlow.value.filter((step) => step.options.includeInStepOverview).map((step) => step.id);
  };

  const getCurrentWizardHasCustomBottomNavigation = () => {
    return getCurrentWizardStep()?.options?.includeCustomPagination ?? false;
  };

  const getWizardStepFromId = (id: string) => {
    return wizardFlow.value.find((step) => step.id === id) || ({} as CreateWizardStepType);
  };

  const getCurrentWizardStep = () => {
    return getWizardStepFromId(currentStep.value);
  };

  const getPreviousWizardStep = () => {
    // Find index of current step in getConditionalWizardFlow array and return the previous step
    const currentWizardStep = getCurrentWizardStep();
    const conditionalWizardFlow = getConditionalWizardFlow();
    const currentStepIndex = conditionalWizardFlow.findIndex((step) => step.id === currentWizardStep.id);
    return conditionalWizardFlow[currentStepIndex - 1];
  };

  const getStepOverviewIds = () => getStepOverviewWizardFlow().map((step) => step.id);

  /** Delegated to pure helpers so the rules stay testable — importing this store pulls the BuildingProject
   * barrel (Apollo client + 3D viewer), which won't construct under vitest's node environment. */
  const getCanJumpToStep = (step: string) => canJumpToStep(step, getStepsDone(), getStepOverviewIds());

  /** Steps the project already satisfies without being walked, for the rail to mark. Never fold into
   * stepsDone — its LAST entry decides what's unlocked, so this would let the user skip steps in front of it. */
  const getPreAnsweredSteps = () =>
    preAnsweredStepIds(getStepOverviewWizardFlow(), createBuildingProject.getProject(), getStepsDone());

  // Setters

  const setWizardFlow = (newWizardFlow: Array<CreateWizardStepType>) => {
    wizardFlow.value = newWizardFlow;
  };

  // Unique type values
  // Uses the unique enum type to identify steps so indexes are not used
  const setCurrentStep = (newCurrentStep: string) => {
    currentStep.value = newCurrentStep;

    // Every navigation path (init, handleChangeStep, handleStepBack, handleStepForward's landOnId) funnels
    // through here, so this is the one place that needs to record a step as visited.
    if (!stepsVisited.value.includes(newCurrentStep)) {
      stepsVisited.value = [...stepsVisited.value, newCurrentStep];
    }
  };

  // Unique type values
  // Uses the unique enum type to identify steps so indexes are not used
  const setStepsDone = (newStepsDone: Array<string>) => {
    stepsDone.value = [...new Set(newStepsDone)];
  };

  const setSpeckleConnectionChoice = (id: number | null) => {
    speckleConnectionChoice.value = id;
  };

  const setTriedToValidate = (newValue: boolean) => {
    triedToValidate.value = newValue;
  };

  // Methods

  const handleChangeStep = (step: string) => {
    // Get current step
    const currentWizardStep = getCurrentWizardStep();

    // Should validate if current step is part of the steps done
    if (getStepsDone().includes(currentWizardStep.id)) {
      // Validate step
      if (currentWizardStep.valid(createBuildingProject.getProject())) {
        // Move to assigned step
        setCurrentStep(step);

        // Reset triedToValidate
        setTriedToValidate(false);
      } else {
        // Set triedToValidate to true
        setTriedToValidate(true);
      }
    } else {
      // Move to next step
      setCurrentStep(step);
    }
  };

  const handleStepForward = () => {
    // Get current step
    const currentWizardStep = getCurrentWizardStep();

    // Validate step
    if (currentWizardStep.valid(createBuildingProject.getProject())) {
      /** Continue passes THROUGH any step the start-project dialog already pre-answered (today: Calculation
       * prerequisites with a flat threshold — see DIALOG_ANSWERABLE_STEP_IDS), marking each done so stepsDone stays a prefix of the path; landOnId is undefined at the end of the flow. */
      const { landOnId, passedIds } = advanceThroughPreAnswered(
        getConditionalWizardFlow(),
        currentWizardStep.id,
        createBuildingProject.getProject()
      );

      // One write, covering the step being left and any crossed on the way out of it. Uses the unique
      // enum type to identify steps so indexes are not used.
      setStepsDone([...stepsDone.value, currentWizardStep.id, ...passedIds]);

      if (landOnId) setCurrentStep(landOnId);

      // Reset triedToValidate
      setTriedToValidate(false);
    } else {
      // Set triedToValidate to true
      setTriedToValidate(true);
    }
  };

  const handleStepBack = () => {
    // Indexes one BEFORE the current step, so it is undefined on the first one — unreachable today only
    // because Back is absent rather than disabled, since isFirstStep and this walk two different flow lists.
    const previousStep = getPreviousWizardStep();
    if (previousStep) handleChangeStep(previousStep.id);
  };

  const init = () => {
    // Clear leftover per-stage standard-value state so it can't leak into this project's Save. asSaved: true
    // also resets the baseline — otherwise an empty map reads as an edit against the previous project's.
    stageDefaultEmissions.setEnabledStages({}, { asSaved: true });
    stageDefaultEmissions.setBuildingTypeFilter(null);

    setCurrentStep(getConditionalWizardFlow()[0].id);
  };

  const reset = () => {
    setCurrentStep(firstStep);
    setStepsDone([]);
    // Cleared after setCurrentStep above: firstStep is a placeholder init() immediately overwrites, so
    // nothing should be recorded as visited from it.
    stepsVisited.value = [];
    setTriedToValidate(false);
    setSpeckleConnectionChoice(null);
  };

  // Watchers

  watch(localization.getCountry, () => {
    setWizardFlow(localization.getCreateWizardFlow());
  });

  // Returns

  return {
    getCurrentStep,
    getStepsDone,
    getStepsVisited,
    getTriedToValidate,
    getSpeckleConnectionChoice,
    getCurrentWizardStep,
    getConditionalWizardFlow,
    getStepOverviewWizardFlow,
    getStepOverviewOrder,
    getCurrentWizardHasCustomBottomNavigation,
    getCanJumpToStep,
    getPreAnsweredSteps,

    setWizardFlow,
    setCurrentStep,
    setStepsDone,
    setTriedToValidate,
    setSpeckleConnectionChoice,

    handleChangeStep,
    handleStepBack,
    handleStepForward,

    init,
    reset,
  };
});

export default useCreateBuildingProjectWizard;
